import React, { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { sortBy } from 'underscore';

import { ManualFeed } from 'views/feed-management/features/manual-feeds/types/manual-feed.type';
import { ManualFeedsCustomer } from 'views/feed-management/features/manual-feeds/types/manual-feeds-customer.type';
import { UpdateManualFeedInput } from 'views/feed-management/features/manual-feeds/types/update-manual-feed-input.type';

import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import { Box, Button, CircularProgress, Grid, useTheme } from '@mui/material';
import Autocomplete from 'components/autocomplete';
import Breadcrumbs from 'components/breadcrumbs';
import Divider from 'components/divider';
import Error from 'components/error';
import FlexBox from 'components/flex-box';
import LoadingSpinner from 'components/loading-spinner';
import Paper from 'components/paper';

import TextField from 'components/text-field';
import FeedManagementTabsHeader from '../../../components/feed-management-tabs-header';

import Switch from 'components/switch';
import CalculateNutrients from 'views/feed-management/features/manual-feeds/components/calculate-nutrients';
import { getManualFeed } from '../api/get-manual-feed';
import { getManualFeedsCustomers } from '../api/get-manual-feeds-customers';
import { getManualFeedsUsers } from '../api/get-manual-feeds-users';
import { putManualFeed } from '../api/update-manual-feed';

type ParamTypes = {
  id: string;
};

type EditManualFeedProps = {};

const PAGE_NAME_ID = 'feedManagement.editManualFeed';

const EditManualFeed: React.FC<EditManualFeedProps> = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const theme = useTheme();

  // FORM
  const {
    handleSubmit: handleSubmitFeed,
    control: controlFeed,
    reset: resetFeed,
  } = useForm<ManualFeed>({
    defaultValues: {
      name: '',
      active: false,
      feedGwpKgCo2EqPerKg: 0,
      digestibleFatGPerKg: 0,
      idealProteinNitrogen: 0,
      nonIdealProteinNitrogen: 0,
      scalingFactorFeedIntake: 0,
      scalingFactorWeightGain: 0,
      assignedUsers: [],
      assignedCompanies: [],
    },
    shouldUnregister: false,
  });

  const watch = useWatch({
    control: controlFeed,
  });

  // FETCH DATA
  const { data: customers } = useGet(
    ['getManualFeedsCustomers', account.company!.companyId],
    ({ queryKey }) => getManualFeedsCustomers(String(queryKey[1])),
    {
      select: (data) => sortBy(data, 'name'),
      enabled: !!account.company?.companyId,
    }
  );

  const { data: users } = useGet(
    ['getManualFeedsUsers', account.company!.companyId],
    ({ queryKey }) => getManualFeedsUsers(String(queryKey[1])),
    {
      select: (data) => sortBy(data, 'name'),
      enabled: !!account.company?.companyId,
    }
  );

  const {
    data: feed,
    error,
    isLoading,
    isError,
  } = useGet(
    ['getManualFeed', account.company!.companyId, params.id],
    ({ queryKey }) => getManualFeed(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!account.company?.companyId && !!customers && !!users,
    }
  );

  useEffect(() => {
    if (feed) resetFeed(feed);
  }, [feed, resetFeed]);

  // POST DATA
  const updateFeed = usePost<ManualFeed, UpdateManualFeedInput>(
    (request) => putManualFeed(account.company!.companyId, feed!.id, request),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.setQueryData(['getManualFeed'], response);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // HANDLERS

  const handleUpdateFeed = (request: ManualFeed) => {
    let feed = {
      name: request.name,
      active: request.active,
      companies: !!request.assignedCompanies ? request.assignedCompanies.map((c) => c.id) : [],
      users: !!request.assignedUsers ? request.assignedUsers.map((u) => u.id) : [],
      feedGwpKgCo2EqPerKg: Number(request.feedGwpKgCo2EqPerKg),
      digestibleFatGPerKg: Number(request.digestibleFatGPerKg),
      idealProteinNitrogen: Number(request.idealProteinNitrogen),
      nonIdealProteinNitrogen: Number(request.nonIdealProteinNitrogen),
      scalingFactorFeedIntake: Number(request.scalingFactorFeedIntake),
      scalingFactorWeightGain: Number(request.scalingFactorWeightGain),
    };

    updateFeed.mutate(feed);
  };

  // RENDERS

  return isError ? (
    <Error error={error} />
  ) : !feed || isLoading ? (
    <LoadingSpinner />
  ) : (
    <>
      <FeedManagementTabsHeader
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: feed.name,
                url: feed.id,
              },
            ]}
          />
        }
      />

      <form>
        <Paper transparent>
          <Grid container spacing={4} columnGap={8}>
            <Paper sx={{ p: 5, width: '100%' }}>
              <Box display={'flex'} flexWrap={'wrap'} alignItems={'center'} justifyContent={'space-between'}>
                <Box display={'flex'}>
                  <TextField
                    sx={{ pr: 4 }}
                    fullWidth
                    dark
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.nameLabel` })}
                    controller={{
                      name: 'name',
                      control: controlFeed,
                      rules: {
                        required: intl.formatMessage({ id: 'form.rules.required' }),
                      },
                    }}
                  />
                  <Switch
                    dark
                    label={
                      watch.active
                        ? intl.formatMessage({ id: `${PAGE_NAME_ID}.activeLabel` })
                        : intl.formatMessage({ id: `${PAGE_NAME_ID}.inactiveLabel` })
                    }
                    controller={{
                      name: 'active',
                      control: controlFeed,
                    }}
                  />
                </Box>
                <Box>
                  <CalculateNutrients manualFeedId={feed.id} />
                </Box>
              </Box>
              <Divider dark />
              <Grid p={theme.spacing(6, 0)} container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    dark
                    fullWidth
                    type="number"
                    endAdornment={'kg CO₂ eq/kg'}
                    controller={{
                      name: 'feedGwpKgCo2EqPerKg',
                      control: controlFeed,
                      rules: {
                        required: intl.formatMessage({ id: 'form.rules.required' }),
                        validate: {
                          positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                        },
                      },
                    }}
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.list.feedGwpLabel` })}
                    sx={{ mb: { xs: 4, md: 0 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    dark
                    fullWidth
                    type="number"
                    endAdornment={'g/kg'}
                    controller={{
                      name: 'digestibleFatGPerKg',
                      control: controlFeed,
                      rules: {
                        required: intl.formatMessage({ id: 'form.rules.required' }),
                        validate: {
                          positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                          between: (v) =>
                            parseInt(v) >= 200 && parseInt(v) <= 450
                              ? true
                              : intl.formatMessage({ id: 'form.rules.between' }, { lower: '200', upper: '450' }),
                        },
                      },
                    }}
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.list.digestibleFatLabel` })}
                    sx={{ mb: { xs: 4, md: 0 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    dark
                    fullWidth
                    type="number"
                    endAdornment={'%'}
                    controller={{
                      name: 'idealProteinNitrogen',
                      control: controlFeed,
                      rules: {
                        required: intl.formatMessage({ id: 'form.rules.required' }),
                        validate: {
                          positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                          between: (v) =>
                            parseFloat(v) >= 1.5 && parseFloat(v) <= 3.5
                              ? true
                              : intl.formatMessage({ id: 'form.rules.between' }, { lower: '1.5', upper: '3.5' }),
                        },
                      },
                    }}
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.list.idealProteinNitrogenLabel` })}
                    sx={{ mb: { xs: 4, md: 0 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    dark
                    fullWidth
                    type="number"
                    endAdornment={'%'}
                    controller={{
                      name: 'nonIdealProteinNitrogen',
                      control: controlFeed,
                      rules: {
                        required: intl.formatMessage({ id: 'form.rules.required' }),
                        validate: {
                          positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                          between: (v) =>
                            parseFloat(v) >= 2.0 && parseFloat(v) <= 5.0
                              ? true
                              : intl.formatMessage({ id: 'form.rules.between' }, { lower: '2.0', upper: '5.0' }),
                        },
                      },
                    }}
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.list.nonIdealProteinNitrogenLabel` })}
                  />
                </Grid>
              </Grid>
              <Divider dark />
              <Grid container spacing={4} pt={4}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    dark
                    fullWidth
                    type="number"
                    controller={{
                      name: 'scalingFactorFeedIntake',
                      control: controlFeed,
                      rules: {
                        required: intl.formatMessage({ id: 'form.rules.required' }),
                        validate: {
                          positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                          between: (v) =>
                            parseFloat(v) <= 2
                              ? true
                              : intl.formatMessage({ id: 'form.rules.between' }, { lower: '0.0', upper: '2.0' }),
                        },
                      },
                    }}
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.list.scalingFactorFeedIntake` })}
                    sx={{ mb: { xs: 4, md: 0 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    dark
                    fullWidth
                    type="number"
                    controller={{
                      name: 'scalingFactorWeightGain',
                      control: controlFeed,
                      rules: {
                        required: intl.formatMessage({ id: 'form.rules.required' }),
                        validate: {
                          positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                          between: (v) =>
                            parseFloat(v) <= 2
                              ? true
                              : intl.formatMessage({ id: 'form.rules.between' }, { lower: '0.0', upper: '2.0' }),
                        },
                      },
                    }}
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.list.scalingFactorWeightGain` })}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ width: '100%', paddingTop: 4 }}>
              <FlexBox>
                <Autocomplete
                  dark
                  fullWidth
                  multiple
                  limitTags={4}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.customers` })}
                  options={customers ?? []}
                  getOptionLabel={(option) => `${(option as ManualFeedsCustomer).name} (${(option as ManualFeedsCustomer).id})`}
                  controller={{
                    control: controlFeed,
                    name: 'assignedCompanies',
                  }}
                />
                {/* We are hidding the users for now and will be sending an empty array to the backend. This will be revisited in the future.
                Link to PBI for more details: Product Backlog Item 1771106: [Feed Management] Feeds on company level only */}
                {/* <Autocomplete
                  dark
                  multiple
                  fullWidth
                  limitTags={4}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.users` })}
                  name="users"
                  options={users ?? []}
                  getOptionLabel={(option) => `${(option as ManualFeedsUser).name} (${(option as ManualFeedsUser).id})`}
                  controller={{
                    control: controlFeed,
                    name: 'assignedUsers',
                  }}
                /> */}
              </FlexBox>
            </Paper>
          </Grid>

          <Box display="flex" justifyContent="flex-end" paddingTop={4}>
            <Button
              type="button"
              onClick={handleSubmitFeed(handleUpdateFeed)}
              disabled={updateFeed.isPending}
              startIcon={updateFeed.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
              <FormattedMessage
                id="general.submit"
                values={{ type: intl.formatMessage({ id: 'feedManagement.navigation.listNutrients' }) }}
              />
            </Button>
          </Box>
        </Paper>
      </form>
    </>
  );
};

export default EditManualFeed;
